import {
  isProvisionalIdentifier,
  formatCurrency,
  isDateObject,
  uuidv4,
  castToBoolean,
} from '../util';

class Claim {
  constructor(data) {
    data = data || {};
    this.id = data.id || null;
    this.app_uuid = data.app_uuid || null;
    this.category = data.category || null;
    this.claim_categories_id = data.claim_categories_id || (this.category && this.category.id) || null;
    this.claim_sources_id = Number(data.claim_sources_id) || null;
    this.type = data.type || null;
    this.claim_types_id = data.claim_types_id || (this.type && this.type.id) || null;
    this.claim_types_name = data.claim_types_name || null;
    this.status = data.status || null;
    this.claim_statuses_id = data.claim_statuses_id || (data.status && data.status.id) || null;
    this.claim_statuses_constant = data.claim_statuses_constant || null;
    this.position = data.position || null;
    this.events_staffing_days_positions_id = data.events_staffing_days_positions_id
      || (this.position && this.position.id)
      || null;
    this.event = data.event || null;
    this.events_id = data.events_id || (this.event && this.event.id) || null;
    this.payroll_periods_id = data.payroll_periods_id || null;
    this.payrollPeriod = data.payrollPeriod || null;
    this.staffing_users_id = data.staffing_users_id || null;
    this.receiptDocument = data.receiptDocument || null;
    this.receipt_documents_id = data.receipt_documents_id
      || (data.receiptDocument && data.receiptDocument.id)
      || null;
    this.claim_date = data.claim_date || null;
    this.description = data.description || null;
    this.units = parseFloat(data.units) || null;
    this.unit_rate = data.unit_rate || null;
    this.amount = data.amount || null;
    this.created_at = data.created_at || null;
    this.updated_at = data.updated_at || null;
    this.auto_approved = data.auto_approved || null;
    this.approved_at = data.approved_at || null;
    this.approved_by = data.approved_by || null;
    this.approved_note = data.approved_note || null;
    this.declined_at = data.declined_at || null;
    this.declined_by = data.declined_by || null;
    this.declined_note = data.declined_note || null;
    this.claim_comments = data.claim_comments || null;
    this.withdrawn_at = data.withdrawn_at || null;
    this.processed_time = data.processed_time || null;
    this.claim_start_time = data.claim_start_time || null;
    this.claim_end_time = data.claim_end_time || null;
    this.attested_at = data.attested_at || null;

    this.receiptFileForUpload = data.receiptFileForUpload || null;
    this.receiptFileDataURL = data.receiptFileDataURL || null;

    this.isResubmitted = data.isResubmitted || null;
    this.isWithdrawn = data.isWithdrawn || null;

    this.pay_date = data.pay_date || null;
    this.pay_department_comments = data.pay_department_comments || null;
    this.paid_amount = data.paid_amount || null;
    this.routeSegment = data.routeSegment || null;
    this.route_segments_id = data.route_segments_id || null;
    this.primary_claim = data.primary_claim;
    this.no_receipt_explanation = data.no_receipt_explanation || null;
    this.noReceiptAvailable = (data.no_receipt_explanation != null);
    this.vendor_name = data.vendor_name || null;
    this.transaction_date = data.transaction_date || null;

    this.venue_name = data.venue_name || null;
    this.positionType = data.positionType || null;

    this._enqueued = false;
  }

  get isSynced() {
    return !(isProvisionalIdentifier(this.id));
  }

  set enqueued(val) {
    this._enqueued = val;
  }

  get enqueued() {
    return this._enqueued;
  }

  get isPrimaryClaim() {
    return this.events_staffing_days_positions_id !== null
      && this.events_staffing_days_positions_id !== 0
      && castToBoolean(this.primary_claim);
  }

  get isSecondaryClaim() {
    return !this.isPrimaryClaim;
  }

  get isApproved() {
    return this.status && (this.status.constant == 'CLAIM_APPROVED' || this.status.constant == 'CLAIM_PROCESSED');
  }

  get isDeclined() {
    return this.status && this.status.constant == 'CLAIM_DECLINED';
  }

  get isDeclinedFinal() {
    return this.status && this.status.constant == 'CLAIM_DECLINED_FINAL';
  }

  get isSubmitted() {
    return this.isPending;
  }

  get isPending() {
    return this.status && this.status.constant == 'CLAIM_PENDING';
  }

  get isPaid() {
    return this.status && this.status.constant == 'CLAIM_PAID';
  }

  get descriptiveTitle() {
    let titleParts = [];
    if (this.category) {
      titleParts.push(this.category.name);
    }
    if (this.type) {
      titleParts.push(this.type.name);
    }
    if (this.category && this.category.isAmountBased && this.amount !== null) {
      titleParts.push(formatCurrency(this.amount));
    }
    let title = titleParts.join(' - ');
    return title;
  }

  get claimDurationInSeconds() {
    let duration = 0;
    if (isDateObject(this.claim_start_time) && isDateObject(this.claim_end_time)) {
      let start = this.claim_start_time.getTime();
      let end = this.claim_end_time.getTime();
      duration = (parseFloat(end) - parseFloat(start)) / 1000.0;
      let secsInDay = 24 * 3600;
      duration = duration + (secsInDay * 3) % secsInDay;
    }
    return duration;
  }

  get claimDurationInHours() {
    return this.claimDurationInSeconds / 3600.00;
  }

  get isManualClaim() {
    return this.claim_sources_id === 2;
  }
}

export default Claim;
